// ============================================================================
// FSA multi-column — formula-column recompute helper
// ============================================================================
// Extracted from MultiColumnClassTable's cell-edit Phase B so the same
// logic can run BOTH on cell edit AND on save. The save path runs this
// over every class row + every line item right before the workspace is
// stringified, so the persisted JSON always reflects what the UI shows
// — WYSIWYS: what you see is what you save (and therefore print).
//
// Without this on save, paths that don't fire Phase B (TB-only
// classification, legacy data, "open + save without editing", etc.)
// would leave column_values[net_closing] / column_values[net_py]
// missing — the UI covers the gap visually via the render-time
// drift_formula fallback, but the saved blob is incomplete and the
// PDF prints blank for those cells.

import { evaluateFormula } from './formulaEvaluator';
import { autoRound, round2, roundedOperandMap } from '../../shared/rounding';
import { ROUNDED_SUFFIX } from './noteClassDataResolver';
import type { FormatColumnSpec } from 'api/types/fsa/template.types';

type RoundingFactorKey = string | null | undefined;

/**
 * Re-evaluate every formula-driven column for a single row's column_values
 * (a class row OR a line item — same shape).
 *
 * Formula-driven = either
 *   • `is_computed: true` + a `formula` (e.g. gross_closing = opening + add − del), OR
 *   • a `drift_formula` (e.g. net_closing = gross_closing − dep_closing) —
 *     promoted to a compute formula in this pass.
 *
 * For each such column the actual is the formula's result against the row's
 * column_values; the __rounded twin is the formula evaluated against the
 * row's ROUNDED operands (roundedOperandMap), to 2 decimals.
 *
 * Returns a fresh object — never mutates the input.
 */
export function recomputeFormulaColumns(
  columnValues: Record<string, number> | null | undefined,
  visibleColumns: FormatColumnSpec[],
  roundingFactorKey: RoundingFactorKey,
): Record<string, number> {
  const next: Record<string, number> = { ...(columnValues ?? {}) };

  // FSA-2.2 — column lookup for the evaluator's is_deduction flag.
  // Columns marked is_deduction get their value negated when resolved
  // as a formula operand. Lets PPE-style specs use '+' uniformly.
  const colByCode: Record<string, FormatColumnSpec> = {};
  for (const col of visibleColumns) colByCode[col.code] = col;

  // Rounded operands: each amount column's __rounded (possibly the auditor's
  // adjustment), else its actual auto-rounded.
  const roundedOperands = roundedOperandMap(next, visibleColumns, roundingFactorKey as Parameters<typeof autoRound>[1], ROUNDED_SUFFIX);

  // Walk columns in declared order so a downstream computed column
  // (e.g. net_closing) sees the freshly-computed upstream value
  // (e.g. dep_closing). column_spec ordering is the contract.
  for (const col of visibleColumns) {
    const expr = (col.is_computed && col.formula) ? col.formula : col.drift_formula;
    if (!expr) continue;
    const v = evaluateFormula(expr, next, colByCode);
    next[col.code] = v;
    if (col.data_type === 'amount') {
      // The rounded total is the formula over the ROUNDED cells — it adds up
      // across the row and follows a hand-adjusted rounded cell (the actual
      // total rounded on its own could differ, and ignored such edits).
      const rounded = round2(evaluateFormula(expr, roundedOperands, colByCode));
      next[col.code + ROUNDED_SUFFIX] = rounded;
      roundedOperands[col.code] = rounded;
    }
  }

  return next;
}
